import { Capacitor } from '@capacitor/core';
import { ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentFirebaseUser } from '@/utils/firebase';
import {
  requiresNativeSubscriptionGate,
} from '@/utils/nativeSubscriptions';
import {
  getAiUsageTierForUid,
  setCachedNativeSubActiveForUid,
} from '@/utils/aiTokenEntitlements';
import { isPayPalWebShell } from '@/utils/payPalWebUi';
import { NativeSubscriptionOptions, SubscriptionLegalCopy } from '@/components/NativeSubscriptionOptions';
import { WebSubscriptionGate, subscribePrimaryButtonClass } from '@/components/WebSubscriptionGate';

const subscribeCheckoutUrl = (process.env.REACT_APP_SUBSCRIBE_CHECKOUT_URL || '').trim();

/**
 * Shown on Home when the daily AI token budget is exhausted: message + same subscribe paths as Account.
 */
export function ChatLimitSubscribePanel({ uid }) {
  const tier = uid ? getAiUsageTierForUid(uid) : 'freemium';
  const needsSubscribeUi = tier === 'trial' || tier === 'freemium';
  const nativeIapConfigured = requiresNativeSubscriptionGate();
  const showWebSubscribe = Boolean(subscribeCheckoutUrl);
  const showWebPayPalSubscribe = Boolean(uid) && needsSubscribeUi && isPayPalWebShell();
  const currentUser = getCurrentFirebaseUser();

  const handleWebSubscribeClick = () => {
    if (!subscribeCheckoutUrl) {
      toast.message('Set REACT_APP_SUBSCRIBE_CHECKOUT_URL for optional web checkout.');
      return;
    }
    window.open(subscribeCheckoutUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="rounded-3xl border border-theme-secondary bg-theme-light p-6 shadow-ambient mx-auto max-w-sm w-full text-center space-y-4"
      data-testid="home-chat-limit-panel"
    >
      <p className="font-body text-sm text-primary leading-relaxed px-1">
        You have reached your daily chat limit — please subscribe to continue.
      </p>
      {!needsSubscribeUi ? (
        <p className="font-body text-xs text-secondary">Your limit resets at midnight (local time).</p>
      ) : (
        <div className="flex flex-col items-center gap-2 pt-1">
          {Capacitor.isNativePlatform() && nativeIapConfigured ? (
            <div className="w-full space-y-3">
              <NativeSubscriptionOptions
                firebaseUid={currentUser?.uid}
                onActivated={() => {
                  if (currentUser?.uid) setCachedNativeSubActiveForUid(currentUser.uid, true);
                }}
                centered
                buttonClassName="w-full rounded-[1.5rem] border border-theme-secondary bg-app px-4 py-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 disabled:opacity-50"
              />
              <SubscriptionLegalCopy centered />
            </div>
          ) : null}
          {showWebPayPalSubscribe && showWebSubscribe ? (
            <button
              type="button"
              onClick={handleWebSubscribeClick}
              className={`${subscribePrimaryButtonClass} mx-auto`}
              data-testid="home-limit-subscribe-web-btn"
            >
              <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
              Subscribe
              <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
            </button>
          ) : null}
          {showWebPayPalSubscribe ? <WebSubscriptionGate hideIntro /> : null}
        </div>
      )}
    </div>
  );
}
